import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, SectionCard, Pill, ActionButton, EmptyState, LoadingState, AlertBanner } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import ReadinessReport from "@/components/workspace/ReadinessReport";
import { ExistingDocumentReuse } from "@/components/documents/ExistingDocumentReuse";
import PublishSendDialog from "@/components/workspace/PublishSendDialog";

const JUR_OPTIONS = [
  { token: "US", label: "United States" }, { token: "UK", label: "United Kingdom" },
  { token: "AU", label: "Australia" }, { token: "CA", label: "Canada" },
  { token: "DE", label: "Germany" }, { token: "FR", label: "France" },
  { token: "NL", label: "Netherlands" }, { token: "TH", label: "Thailand" },
];
const SCOPE_OPTIONS = [
  { key: "current_residence", label: "Country of Residence" },
  { key: "citizenship", label: "Citizenship" },
  { key: "claimed_tax_residence", label: "Claimed Tax Residence" },
  { key: "pension", label: "Pension Jurisdiction" },
  { key: "investment", label: "Investment Jurisdiction" },
  { key: "property", label: "Property Jurisdiction" },
  { key: "business", label: "Business Jurisdiction" },
  { key: "trust", label: "Trust Jurisdiction" },
  { key: "income", label: "Income Jurisdiction" },
];
const MODULE_TONES = {
  universal_core: "info", residence: "purple", jurisdiction: "success",
  subject: "warning", treaty: "neutral", staff_added: "danger",
};
const MODULE_BG = {
  universal_core: "bg-blue-50", residence: "bg-violet-50", jurisdiction: "bg-emerald-50",
  subject: "bg-amber-50", treaty: "bg-slate-50", staff_added: "bg-rose-50",
};
const jurLabel = (t) => (JUR_OPTIONS.find((j) => j.token === t) || {}).label || t;

export default function StrategicReview() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [modules, setModules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [docReqs, setDocReqs] = useState([]);
  const [assignments, setAssignments] = useState([]);
  // STR lifecycle state from project_str_state()
  const [strState, setStrState] = useState(null);
  // Increment 3: selected engagement_id for cycle switching
  const [selectedEngagementId, setSelectedEngagementId] = useState(sp.get("engagement_id") || null);
  // Ref to scroll to the snapshots list after assignment creation
  const snapshotsRef = useRef(null);
  // Auto-poll interval ref for analysis_running state
  const strPollRef = useRef(null);

  // assembly state
  const [templateCode, setTemplateCode] = useState("");    // initialized from workspace-init hint
  const [staffJur, setStaffJur] = useState({});           // { scope: [tokens] }
  const [topics, setTopics] = useState([]);               // subject topic values
  const [taxYears, setTaxYears] = useState("");           // comma string
  const [addModules, setAddModules] = useState([]);
  const [removeModules, setRemoveModules] = useState([]);
  const [addScope, setAddScope] = useState("current_residence");
  const [addToken, setAddToken] = useState("US");

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [tab, setTab] = useState(() => sp.get("tab") || "assemble");
  const [publishFor, setPublishFor] = useState(null);

  const refreshStrState = useCallback(async () => {
    try {
      const params = selectedEngagementId ? { engagement_id: selectedEngagementId } : {};
      const st = await api.get(`/strategic-review/state/${clientId}`, { params });
      setStrState(st.data);
    } catch { /* non-fatal */ }
  }, [clientId, selectedEngagementId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const stateParams = selectedEngagementId ? { engagement_id: selectedEngagementId } : {};
      const [tpl, mod, qs, dr, asg] = await Promise.all([
        api.get("/strategic-review/templates"),
        api.get("/strategic-review/modules"),
        api.get("/strategic-review/questions"),
        api.get("/strategic-review/document-requirements"),
        api.get("/strategic-review/assignments", { params: { client_id: clientId, ...(selectedEngagementId ? { engagement_id: selectedEngagementId } : {}) } }),
      ]);
      setTemplates(tpl.data.templates || []);
      setModules(mod.data.modules || []);
      setQuestions(qs.data.questions || []);
      setDocReqs(dr.data.document_requirements || []);
      setAssignments(asg.data.assignments || []);
      // Load STR lifecycle state (read-only, best-effort)
      try {
        const st = await api.get(`/strategic-review/state/${clientId}`, { params: stateParams });
        setStrState(st.data);
      } catch { /* no STR state is non-fatal */ }
      try {
        const c = await api.get(`/crm/contacts/${clientId}`);
        setClient(c.data.contact || c.data);
      } catch { /* client optional */ }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load Strategic Review Library");
    } finally {
      setLoading(false);
    }
  }, [clientId, selectedEngagementId]);

  useEffect(() => { load(); }, [load]);

  // Initialize templateCode from the workspace-init recommendation (same logic as auto-assignment),
  // falling back to the first available active template. Never overrides a staff selection.
  useEffect(() => {
    if (templateCode) return;                               // staff already selected one
    const rec = strState?.recommended_template_code;
    if (rec) { setTemplateCode(rec); return; }
    if (templates.length > 0) { setTemplateCode(templates[0].code); }
  }, [strState, templates]);                               // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill review year from the canonical projected year (engagement.tax_year → cycle max → date year).
  // Placeholder "2024, 2025" is a hint only; actual value derives from the engagement record.
  useEffect(() => {
    if (taxYears || !strState?.review_year) return;
    setTaxYears(String(strState.review_year));
  }, [strState?.review_year]);                            // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-poll strState every 6s when Allison analysis is running; clear when it settles
  useEffect(() => {
    if (strState?.state === "analysis_running") {
      strPollRef.current = setInterval(refreshStrState, 6000);
    } else {
      clearInterval(strPollRef.current);
    }
    return () => clearInterval(strPollRef.current);
  }, [strState?.state, refreshStrState]);

  // Launch the governed STR analysis pipeline (staff-initiated)
  const [launching, setLaunching] = useState(false);
  const launchAnalysis = useCallback(async () => {
    const cid = strState?.completion_id;
    if (!cid) { toast.error("No submitted questionnaire found"); return; }
    setLaunching(true);
    try {
      await api.post(`/strategic-review/completion/${cid}/launch-analysis`);
      toast.success("Strategic Review analysis launched");
      await refreshStrState();
      setTab("findings");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const errs = (typeof detail === "object" ? detail?.errors : null) || [];
      toast.error(errs.length ? errs.join("; ") : (formatApiErrorDetail(detail) || "Launch failed"));
    } finally {
      setLaunching(false);
    }
  }, [strState, refreshStrState]);

  // Increment 3: Mark cycle complete (management-level, idempotent)
  const [completing, setCompleting] = useState(false);
  const completeCycle = useCallback(async () => {
    const eid = strState?.engagement_id;
    if (!eid) { toast.error("No engagement ID found for this cycle"); return; }
    setCompleting(true);
    try {
      await api.post(`/strategic-review/cycle/${eid}/complete`);
      toast.success("Strategic Review cycle marked as complete");
      await refreshStrState();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" ? detail?.message : detail)
                  || formatApiErrorDetail(detail) || "Failed to mark cycle complete";
      toast.error(msg);
    } finally {
      setCompleting(false);
    }
  }, [strState, refreshStrState]);

  const subjectTopics = useMemo(() => modules
    .filter((m) => m.module_type === "subject" && m.trigger_rules?.op === "topic")
    .map((m) => ({ value: m.trigger_rules.value, label: m.name, code: m.code })), [modules]);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    try {
      const body = {
        engagement_id: selectedEngagementId, client_id: clientId, template_code: templateCode,
        jurisdictions: staffJur, topics,
        tax_years: taxYears.split(",").map((y) => y.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n)),
        add_modules: addModules, remove_modules: removeModules,
      };
      const { data } = await api.post("/strategic-review/assign/preview", body);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  }, [selectedEngagementId, clientId, templateCode, staffJur, topics, taxYears, addModules, removeModules]);

  useEffect(() => { if (!loading && templateCode) runPreview(); /* eslint-disable-next-line */ }, [loading, templateCode, staffJur, topics, addModules, removeModules]);

  const doAssign = async () => {
    setAssigning(true);
    try {
      const body = {
        engagement_id: selectedEngagementId, client_id: clientId, template_code: templateCode,
        jurisdictions: staffJur, topics,
        tax_years: taxYears.split(",").map((y) => y.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n)),
        add_modules: addModules, remove_modules: removeModules,
      };
      const { data } = await api.post("/strategic-review/assign", body);
      toast.success("Strategic Review assembled and snapshotted");
      setAssignments((a) => [data.assignment, ...a]);
      // Refresh STR lifecycle state — transitions view to assignment_pending
      await refreshStrState();
      setTimeout(() => snapshotsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const publishAssignment = async (aid) => {
    try {
      const { data } = await api.post(`/strategic-review/assignments/${aid}/publish`);
      toast.success(data.already ? "Already available in the client portal" : "Made available in the client portal");
      setAssignments((list) => list.map((a) => (a.id === aid ? { ...a, status: data.status } : a)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Publish failed");
    }
  };

  const addJurLink = () => {
    setStaffJur((prev) => {
      const cur = new Set(prev[addScope] || []);
      cur.add(addToken);
      return { ...prev, [addScope]: [...cur] };
    });
  };
  const removeJurLink = (scope, token) => setStaffJur((prev) => ({
    ...prev, [scope]: (prev[scope] || []).filter((t) => t !== token),
  }));
  const toggleTopic = (v) => setTopics((t) => (t.includes(v) ? t.filter((x) => x !== v) : [...t, v]));
  const toggleModule = (code, list, setList) => setList((l) => (l.includes(code) ? l.filter((x) => x !== code) : [...l, code]));

  useEffect(() => {
    const p = sp.get("publish");
    if (p) setPublishFor(p);
  }, [sp]);

  if (loading) return <LoadingState label="Loading Strategic Review Library…" />;

  return (
    <div className="max-w-7xl mx-auto" data-testid="strategic-review-page">
      <PageHeader
        title="Strategic Tax Review"
        description={client ? `Assemble & preview for ${client.full_name || client.name || clientId}` : "Canonical questionnaire assembly"}
        action={<ActionButton variant="ghost" icon="ArrowLeft" onClick={() => navigate(`/client-workspace/${clientId}`)} data-testid="sr-back">Back to Workspace</ActionButton>}
      />

      <Tabs value={tab} onValueChange={setTab} className="mt-4">
        <TabsList data-testid="sr-top-tabs">
          <TabsTrigger value="assemble" data-testid="sr-tab-assemble">Assemble</TabsTrigger>
          {/* P0 Viewer: Submission tab — visible when this client has a submitted completion */}
          {strState?.completion_id && (
            <TabsTrigger value="submission" data-testid="sr-tab-submission">Submitted Answers</TabsTrigger>
          )}
          <TabsTrigger value="findings" data-testid="sr-tab-findings">Allison Findings</TabsTrigger>
          <TabsTrigger value="readiness" data-testid="sr-tab-readiness">Readiness &amp; Report</TabsTrigger>
          <TabsTrigger value="library" data-testid="sr-tab-library">Strategic Review Library</TabsTrigger>
        </TabsList>

        {/* ───────────────── ASSEMBLE ───────────────── */}
        <TabsContent value="assemble" className="mt-4">
          {/* Increment 3: cycle selector — shown when multiple cycles exist */}
          {strState?.available_cycles?.length > 1 && (
            <div className="mb-4">
              <StrCycleSelector
                availableCycles={strState.available_cycles}
                selectedEngagementId={selectedEngagementId}
                onSelect={(eid) => setSelectedEngagementId(eid)}
              />
            </div>
          )}
          {/* Phase 5B: compact cycle history timeline — shown whenever a cycle has been published */}
          {strState?.available_cycles && (
            <StrCycleTimeline
              cycles={strState.available_cycles}
              selectedEngagementId={selectedEngagementId}
            />
          )}
          {/* STR lifecycle state banner — shown above assembly controls */}
          {strState && (
            <StrStateGateBanner
              strState={strState}
              onScrollToAssignment={() => snapshotsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              onLaunchAnalysis={launchAnalysis}
              onGoToFindings={() => setTab("findings")}
              launching={launching}
              onMarkComplete={completeCycle}
              completing={completing}
              onGoToEngagementLetter={() => navigate(`/client-workspace/${clientId}?tab=engagement_letters`)}
            />
          )}
          {/* Evidence availability panel — shown when evidence summary is available */}
          {strState?.evidence_summary && (
            <EvidenceSummaryPanel
              evidenceSummary={strState.evidence_summary}
              strState={strState}
              className="mt-3"
            />
          )}
          <div className={`grid grid-cols-1 lg:grid-cols-5 gap-5 ${strState ? "mt-4" : ""}`}>
            {/* Left: context controls */}
            <div className="lg:col-span-2 space-y-5">
              <SectionCard title="Questionnaire" icon="ClipboardList">
                {templates.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2" data-testid="sr-template-config-error">
                    <div className="flex items-center gap-2 text-amber-800">
                      <Icons.AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                      <span className="text-sm font-medium">STR Configuration Requires Attention</span>
                    </div>
                    <p className="text-xs text-amber-700">No active STR questionnaire template is available. To resolve this, a staff administrator should run the STR seed sequence from the admin panel:</p>
                    <ol className="text-xs text-amber-700 pl-4 space-y-0.5 list-decimal">
                      <li><code className="bg-amber-100 px-1 rounded">POST /api/strategic-review/admin/seed</code></li>
                      <li><code className="bg-amber-100 px-1 rounded">POST /api/strategic-review/admin/seed-templates</code></li>
                      <li><code className="bg-amber-100 px-1 rounded">POST /api/strategic-review/admin/seed-organizer-v1</code></li>
                    </ol>
                    <p className="text-xs text-amber-600 mt-1">The STR is commercially authorized and production-ready. This is a configuration issue only — it does not affect Theo's eligibility.</p>
                  </div>
                ) : (
                <>
                <Select value={templateCode} onValueChange={setTemplateCode}>
                  <SelectTrigger data-testid="sr-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="mt-3">
                  <label className="text-xs font-medium text-slate-500">Tax Years (comma-separated)</label>
                  <Input value={taxYears} onChange={(e) => setTaxYears(e.target.value)} placeholder="2024, 2025" data-testid="sr-tax-years" className="mt-1" />
                </div>
                </>
                )}
              </SectionCard>

              <SectionCard title="Jurisdiction Discovery" icon="Globe">
                <p className="text-xs text-slate-500 mb-3">Auto-discovered from the client record and merged with staff-provided links. Jurisdiction packages activate on any link; the Thailand & treaty modules require Thailand residence.</p>
                {preview?.context?.provenance && Object.keys(preview.context.provenance).length > 0 ? (
                  <div className="space-y-2 mb-3" data-testid="sr-discovered-jur">
                    {Object.entries(preview.context.jurisdictions).filter(([, v]) => v.length).map(([scope, toks]) => (
                      <div key={scope} className="text-xs">
                        <span className="uppercase tracking-wide text-slate-400">{(SCOPE_OPTIONS.find((s) => s.key === scope) || {}).label || scope}: </span>
                        {toks.map((t) => (
                          <Badge key={t} variant="secondary" className="mr-1" title={preview.context.provenance?.[scope]?.[t] || ""}>{jurLabel(t)}</Badge>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-slate-400 mb-3">No jurisdictions discovered yet.</p>}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-[11px] text-slate-400">Scope</label>
                    <Select value={addScope} onValueChange={setAddScope}>
                      <SelectTrigger data-testid="sr-jur-scope" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{SCOPE_OPTIONS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-[11px] text-slate-400">Jurisdiction</label>
                    <Select value={addToken} onValueChange={setAddToken}>
                      <SelectTrigger data-testid="sr-jur-token" className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{JUR_OPTIONS.map((j) => <SelectItem key={j.token} value={j.token}>{j.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <ActionButton variant="secondary" icon="Plus" onClick={addJurLink} data-testid="sr-jur-add">Add</ActionButton>
                </div>
                {Object.entries(staffJur).some(([, v]) => v.length) && (
                  <div className="mt-3 space-y-1" data-testid="sr-staff-jur">
                    {Object.entries(staffJur).filter(([, v]) => v.length).map(([scope, toks]) => (
                      <div key={scope} className="text-xs flex flex-wrap items-center gap-1">
                        <span className="uppercase tracking-wide text-slate-400">{(SCOPE_OPTIONS.find((s) => s.key === scope) || {}).label}:</span>
                        {toks.map((t) => (
                          <button key={t} onClick={() => removeJurLink(scope, t)} className="inline-flex items-center gap-1 rounded-full bg-slate-100 hover:bg-rose-100 px-2 py-0.5 text-slate-700" data-testid={`sr-staff-jur-${scope}-${t}`}>
                            {jurLabel(t)} <Icons.X className="h-3 w-3" />
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Subject Topics" icon="Tags">
                <p className="text-xs text-slate-500 mb-3">Select active subjects to include the matching modules.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-64 overflow-auto">
                  {subjectTopics.map((t) => (
                    <label key={t.value} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer py-0.5" data-testid={`sr-topic-${t.value}`}>
                      <Checkbox checked={topics.includes(t.value)} onCheckedChange={() => toggleTopic(t.value)} />
                      {t.label}
                    </label>
                  ))}
                </div>
              </SectionCard>
            </div>

            {/* Right: preview + explain assembly */}
            <div className="lg:col-span-3 space-y-5">
              <SectionCard
                title="Explain Assembly"
                icon="ListTree"
                action={<div className="flex items-center gap-2">
                  {previewing && <span className="text-xs text-slate-400">Recomputing…</span>}
                  <Pill tone="info" data-testid="sr-question-count">{preview?.question_count ?? 0} Questions</Pill>
                </div>}
              >
                {!preview ? <EmptyState icon="ListTree" title="No preview yet" /> : (
                  <div className="space-y-1.5" data-testid="sr-explain-assembly">
                    {preview.diagnostics.map((d) => (
                      <div key={d.module} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${d.included ? "bg-emerald-50" : "bg-slate-50 opacity-70"}`} data-testid={`sr-diag-${d.module}`}>
                        {d.included ? <Icons.CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> : <Icons.MinusCircle className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-800">{d.name || d.module}</span>
                            <Badge variant="outline" className="text-[10px]">{d.type}</Badge>
                            {d.included && <span className="text-[11px] text-slate-500">{(preview.modules.find((m) => m.module_code === d.module)?.questions.length ?? 0)} Q</span>}
                          </div>
                          <div className="text-[11px] text-slate-500">Reason: {d.reason || (d.included ? "matched trigger" : "trigger not met")}</div>
                        </div>
                        {!(preview.modules.find((m) => m.module_code === d.module)?.type === "universal_core") && (
                          d.included ? (
                            <button onClick={() => toggleModule(d.module, removeModules, setRemoveModules)} className="text-[11px] text-rose-600 hover:underline shrink-0" data-testid={`sr-remove-${d.module}`}>Remove</button>
                          ) : (
                            <button onClick={() => toggleModule(d.module, addModules, setAddModules)} className="text-[11px] text-blue-600 hover:underline shrink-0" data-testid={`sr-add-${d.module}`}>Add</button>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              {/* Context-aware assignment creation area */}
              {(() => {
                const isReviewReady = !strState || strState.state === "review_ready" || strState.state === "not_initialized";
                const hasActiveAssignment = strState?.assignment_id && !isReviewReady;
                const isHistorical = strState?.is_historical_cycle === true;
                return (
                  <SectionCard title="Questionnaire Preview" icon="FileText"
                    action={
                      <ActionButton
                        variant={isReviewReady ? "primary" : "secondary"}
                        icon={isReviewReady ? "Save" : "RefreshCw"}
                        onClick={doAssign}
                        loading={assigning}
                        disabled={isHistorical}
                        title={isHistorical ? "Historical cycle — read only" : undefined}
                        data-testid="sr-assign-btn"
                      >
                        {isReviewReady ? "Confirm & Create Assignment" : "Reassemble"}
                      </ActionButton>
                    }
                  >
                    {hasActiveAssignment && (
                      <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="str-reassemble-warn">
                        <Icons.AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                        <span>An assembled assignment already exists below. Reassembling creates a new snapshot.</span>
                      </div>
                    )}
                    {!preview || preview.modules.length === 0 ? <EmptyState icon="FileText" title="Nothing assembled yet" /> : (
                      <div className="space-y-4" data-testid="sr-questionnaire-preview">
                        {preview.modules.map((m) => (
                          <div key={m.module_code} className="border border-slate-200 rounded-xl overflow-hidden">
                            <div className={`px-4 py-2 flex items-center gap-2 ${MODULE_BG[m.type] || "bg-slate-50"}`}>
                              <Pill tone={MODULE_TONES[m.type] || "neutral"}>{m.type}</Pill>
                              <span className="font-medium text-slate-800 text-sm">{m.name}</span>
                              <span className="text-[11px] text-slate-400 ml-auto">v{m.module_version} · {m.questions.length} Q</span>
                            </div>
                            <div className="divide-y divide-slate-100">
                              {m.questions.length === 0 ? <div className="px-4 py-2 text-xs text-slate-400 italic">Metadata module (no direct questions)</div> :
                                m.questions.map((q) => (
                                  <div key={q.code} className="px-4 py-2 flex items-start gap-2 text-sm">
                                    <span className="text-slate-800 flex-1">{q.text}</span>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {q.required && <Badge variant="destructive" className="text-[9px]">required</Badge>}
                                      {q.doc_request && <Badge variant="outline" className="text-[9px]"><Icons.Paperclip className="h-2.5 w-2.5 mr-0.5" />doc</Badge>}
                                      {q.ptf_mapping && <Badge variant="secondary" className="text-[9px]">PTF</Badge>}
                                      <span className="text-[10px] text-slate-400">{q.response_type}</span>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div className="text-xs text-slate-500"><strong>{preview.document_requests.length}</strong> document request(s) mapped</div>
                          <div className="text-xs text-slate-500"><strong>{preview.ptf_candidates.length}</strong> Permanent Tax File candidate(s)</div>
                        </div>
                      </div>
                    )}
                    {/* Engagement gate hint — shown near action only when it blocks publication */}
                    {strState && !strState.engagement_gate?.satisfied && (
                      <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600" data-testid="str-engagement-gate-warn">
                        <Icons.Lock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                        <span><strong>Engagement signature pending</strong> — the questionnaire can be assembled now, but publishing to the portal will be available once the engagement letter is signed.</span>
                        <button
                          onClick={() => navigate(`/client-workspace/${clientId}?tab=engagement_letters`)}
                          className="ml-auto shrink-0 text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap"
                          data-testid="str-engagement-gate-deeplink"
                        >
                          View Engagement Letter →
                        </button>
                      </div>
                    )}
                  </SectionCard>
                );
              })()}

              {assignments.length > 0 && (
                <div ref={snapshotsRef}>
                <SectionCard title="Saved Snapshots" icon="History">
                  <div className="space-y-2" data-testid="sr-snapshots">
                    {assignments.map((a) => {
                      const isLatestPendingPublish = strState?.assignment_id === a.id && a.status === "assembled";
                      const isHistorical = strState?.is_historical_cycle === true;
                      return (
                        <div key={a.id} className={`flex items-center gap-3 text-sm border rounded-lg px-3 py-2 ${isLatestPendingPublish ? "border-blue-300 bg-blue-50" : "border-slate-200"}`} data-testid={`sr-snapshot-${a.id.slice(0,8)}`}>
                          {isLatestPendingPublish && <Icons.FileCheck className="h-4 w-4 text-blue-600 shrink-0" />}
                          <Pill tone={a.status === "portal_ready" ? "success" : a.status === "assembled" ? "info" : "neutral"}>{a.status}</Pill>
                          <span className="font-mono text-xs text-slate-500">{a.id.slice(0, 8)}</span>
                          <span className="text-slate-600">{(a.modules || []).length} modules · {(a.questions || []).length} questions</span>
                          <div className="ml-auto flex items-center gap-2">
                            <ActionButton variant="ghost" icon="Eye" onClick={() => navigate(`/client-success/strategic-review/${clientId}/preview/${a.id}`)} data-testid={`sr-preview-${a.id.slice(0,8)}`}>Client Preview</ActionButton>
                            {a.status !== "assembled" && <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1"><Icons.CheckCircle2 className="h-3.5 w-3.5" />In portal</span>}
                            {!isHistorical && (
                              <ActionButton variant={isLatestPendingPublish ? "primary" : "secondary"} icon="Send" onClick={() => setPublishFor(a.id)} data-testid={`sr-publish-${a.id.slice(0,8)}`}>Publish &amp; Send</ActionButton>
                            )}
                          </div>
                          <span className="text-[11px] text-slate-400">{new Date(a.snapshot_at || a.created_at).toLocaleString()}</span>
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ───────────────── SUBMITTED ANSWERS (P0 Viewer) ───────────────── */}
        <TabsContent value="submission" className="mt-4">
          <StrSubmissionViewer strState={strState} clientId={clientId} navigate={navigate} />
        </TabsContent>

        {/* ───────────────── ALLISON FINDINGS (Phase 1C-i, read-only) ───────────────── */}
        <TabsContent value="findings" className="mt-4">
          <AllisonFindings assignments={assignments} clientId={clientId} />
        </TabsContent>

        {/* ───────────────── READINESS & REPORT (Phase 1C-iii) ───────────────── */}
        <TabsContent value="readiness" className="mt-4">
          <ReadinessReport assignments={assignments} clientId={clientId} onGoToTab={setTab} />
        </TabsContent>

        {/* ───────────────── LIBRARY ───────────────── */}
        <TabsContent value="library" className="mt-4">
          <LibraryBrowser templates={templates} modules={modules} questions={questions} docReqs={docReqs} />
        </TabsContent>
      </Tabs>

      {publishFor && (
        <PublishSendDialog open={!!publishFor} onClose={() => setPublishFor(null)}
          assignmentId={publishFor} clientId={clientId}
          onPublished={() => { load(); }} />
      )}
    </div>
  );
}

// ── STR State Gateway Banner ──────────────────────────────────────────────────
// Shown at the top of the Assemble tab to surface the current STR lifecycle
// state and gate conditions (payment, engagement). Does not modify any records.
function StrStateGateBanner({ strState, onScrollToAssignment, onLaunchAnalysis, onGoToFindings, launching, onMarkComplete, completing, onGoToEngagementLetter }) {
  const { state, payment_gate, engagement_gate, current_state_label, is_historical_cycle,
          review_year, cycle_completion_basis } = strState;

  // WS1 — Phase 5A: read receipt for review_complete / cycle_complete states
  const [readReceipt, setReadReceipt] = useState(null);
  useEffect(() => {
    if ((state !== "review_complete" && state !== "cycle_complete") || !strState.client_id || !strState.engagement_id) return;
    api.get(`/strategic-review-engine/client/${strState.client_id}/publications`, {
      params: { engagement_id: strState.engagement_id },
    })
      .then((r) => {
        const pub = (r.data.publications || [])[0];
        if (pub) setReadReceipt({
          first_viewed_at: pub.first_viewed_at || pub.client_viewed_at || null,
          last_viewed_at: pub.last_viewed_at || null,
        });
        else setReadReceipt({});
      })
      .catch(() => {/* non-fatal — receipt silently absent */});
  }, [state, strState.client_id, strState.engagement_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fmtViewDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short", day: "numeric", year: "numeric",
        hour: "numeric", minute: "2-digit",
      });
    } catch { return iso; }
  };

  // Historical cycle — all mutation actions disabled; show read-only notice
  if (is_historical_cycle) return (
    <div className="rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-historical-cycle">
      <Icons.Archive className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-700">
          Historical Cycle — Read Only
          {review_year && <span className="ml-2 text-xs text-slate-500 font-normal">({review_year} Strategic Review)</span>}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          This cycle has been completed and is preserved as an immutable historical record.
          All records, submissions, findings, and publications remain accessible but cannot be modified.
          {cycle_completion_basis === "legacy_allison_delivered" && (
            <span className="ml-1 text-amber-600 font-medium">Completed — Legacy Allison Delivery.</span>
          )}
        </p>
      </div>
    </div>
  );

  if (state === "not_initialized") return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-not-initialized">
      <Icons.AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-amber-800">No accepted STR estimate found</p>
        <p className="text-xs text-amber-600 mt-0.5">Accept an ADV-STRATEGY estimate from the client&apos;s Sales workspace to begin the Strategic Tax Review journey.</p>
      </div>
    </div>
  );

  if (state === "review_ready") return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-review-ready">
      <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-emerald-800">Ready to begin — review the recommended modules and confirm below</p>
        <p className="text-xs text-emerald-600 mt-0.5">
          An accepted ADV-STRATEGY estimate was found. Adjust the template and modules as needed, then click <strong>Confirm &amp; Create Assignment</strong> to create the immutable questionnaire snapshot.
        </p>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0 text-[11px]">
        <span className={`inline-flex items-center gap-1 ${payment_gate?.satisfied ? "text-emerald-700" : "text-amber-700"}`} data-testid="str-payment-gate-status">
          {payment_gate?.satisfied ? <Icons.CheckCircle2 className="h-3 w-3" /> : <Icons.AlertCircle className="h-3 w-3" />}
          Payment: {payment_gate?.satisfied ? "Satisfied" : "Pending"}
        </span>
        <span
          className={`inline-flex items-center gap-1 ${engagement_gate?.satisfied ? "text-emerald-700" : "text-amber-700 cursor-pointer hover:underline"}`}
          data-testid="str-engagement-gate-status"
          onClick={!engagement_gate?.satisfied ? onGoToEngagementLetter : undefined}
          title={!engagement_gate?.satisfied ? "View Engagement Letter" : undefined}
        >
          {engagement_gate?.satisfied ? <Icons.CheckCircle2 className="h-3 w-3" /> : <Icons.AlertCircle className="h-3 w-3" />}
          Engagement: {engagement_gate?.satisfied ? "Active" : "Signature pending"}
        </span>
      </div>
    </div>
  );

  if (state === "assignment_pending") return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-assignment-pending">
      <Icons.FileCheck className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-blue-800">Assignment assembled — ready to publish to client portal</p>
        <p className="text-xs text-blue-600 mt-0.5">
          The questionnaire snapshot is ready. Preview it, then use <strong>Publish &amp; Send</strong> to make it available to the client.
          {!engagement_gate?.satisfied && (
            <span className="ml-1 text-amber-700 font-medium">
              {" "}Engagement signature pending — publishing restricted until engagement is active.{" "}
              <button
                onClick={onGoToEngagementLetter}
                className="font-semibold underline hover:no-underline"
                data-testid="str-assignment-engagement-deeplink"
              >
                View Engagement Letter →
              </button>
            </span>
          )}
        </p>
      </div>
      <button onClick={onScrollToAssignment} className="text-[11px] text-blue-700 hover:underline shrink-0 mt-0.5 whitespace-nowrap" data-testid="str-scroll-to-assignment">
        View assignment ↓
      </button>
    </div>
  );

  if (state === "questionnaire_published") return (
    <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-center gap-3" data-testid="str-state-questionnaire-published">
      <Icons.Send className="h-5 w-5 text-violet-600 shrink-0" />
      <p className="text-sm font-medium text-violet-800">Questionnaire published — awaiting client submission</p>
    </div>
  );

  // ── Post-submission analysis states (Increment 2) ────────────────────────

  if (state === "questionnaire_submitted" || state === "analysis_queued") return (
    <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 flex items-start gap-3" data-testid={`str-state-${state}`}>
      <Icons.Inbox className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-orange-800">Questionnaire submitted — ready for Allison&apos;s Strategic Review analysis</p>
        <p className="text-xs text-orange-700 mt-0.5">
          The client has submitted their questionnaire. Click <strong>Generate Strategic Review</strong> to launch Allison&apos;s analysis. Allison will review all available evidence and produce her findings.
        </p>
      </div>
      <ActionButton variant="primary" icon="Brain" onClick={onLaunchAnalysis} loading={launching}
        data-testid="str-launch-analysis-btn" className="shrink-0">
        Generate Strategic Review
      </ActionButton>
    </div>
  );

  if (state === "analysis_running") return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-analysis-running">
      <Icons.Loader2 className="h-5 w-5 text-blue-600 shrink-0 mt-0.5 animate-spin" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-blue-800">Analysis in progress — Allison is reviewing the Strategic Tax questionnaire</p>
        <p className="text-xs text-blue-600 mt-0.5">This typically takes 1–3 minutes. The page will update automatically.</p>
      </div>
      <button onClick={onGoToFindings} className="text-[11px] text-blue-700 hover:underline shrink-0 mt-0.5 whitespace-nowrap" data-testid="str-view-analysis-status-btn">
        View Analysis Status →
      </button>
    </div>
  );

  if (state === "analysis_complete") return (
    <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-analysis-complete">
      <Icons.Wand2 className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-teal-800">Analysis complete — ready to draft the Strategic Review report</p>
        <p className="text-xs text-teal-600 mt-0.5">Allison has completed her findings. Open the <strong>Allison Findings</strong> tab to review the analysis and generate the draft report.</p>
      </div>
      <button onClick={onGoToFindings} className="text-[11px] text-teal-700 hover:underline font-medium shrink-0 mt-0.5 whitespace-nowrap" data-testid="str-generate-draft-btn">
        Go to Findings &amp; Draft Report →
      </button>
    </div>
  );

  if (state === "management_review") return (
    <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-management-review">
      <Icons.ShieldCheck className="h-5 w-5 text-violet-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-violet-800">Report drafted — awaiting management review and approval</p>
        <p className="text-xs text-violet-600 mt-0.5">The Strategic Review draft has been generated. Open the Allison Findings tab to review, edit, and approve the report.</p>
      </div>
      <button onClick={onGoToFindings} className="text-[11px] text-violet-700 hover:underline font-medium shrink-0 mt-0.5 whitespace-nowrap" data-testid="str-management-review-btn">
        Management Review →
      </button>
    </div>
  );

  // ── Increment 3: review_complete with "Mark Cycle Complete" action ─────────
  if (state === "review_complete") return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-review-complete">
      <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-emerald-800">Strategic Review complete and delivered to client</p>
        <p className="text-xs text-emerald-600 mt-0.5">Management may now formally close this review cycle to preserve it as a historical record and enable a new annual cycle.</p>
        {/* WS1 — Phase 5A: compact read receipt status */}
        {readReceipt !== null && (
          <p className="text-xs mt-1.5 flex items-center gap-1.5" data-testid="str-read-receipt">
            <Icons.Eye className="h-3 w-3 shrink-0 text-emerald-500" />
            {readReceipt.first_viewed_at ? (
              <span className="text-emerald-700">
                First viewed {fmtViewDate(readReceipt.first_viewed_at)}
                {readReceipt.last_viewed_at && readReceipt.last_viewed_at !== readReceipt.first_viewed_at && (
                  <span className="ml-2 text-emerald-500">· Last viewed {fmtViewDate(readReceipt.last_viewed_at)}</span>
                )}
              </span>
            ) : (
              <span className="text-amber-600 font-medium">Not yet viewed by client</span>
            )}
          </p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0 mt-0.5">
        <button onClick={onGoToFindings} className="text-[11px] text-emerald-700 hover:underline font-medium whitespace-nowrap" data-testid="str-view-final-report-btn">
          View Final Report →
        </button>
        <button onClick={onMarkComplete} disabled={completing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 whitespace-nowrap transition-colors"
          data-testid="str-mark-cycle-complete-btn">
          {completing ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Flag className="h-3 w-3" />}
          Mark Cycle Complete
        </button>
      </div>
    </div>
  );

  // ── Increment 3: cycle_complete with "Start New Review Cycle" CTA ─────────
  if (state === "cycle_complete") return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-3" data-testid="str-state-cycle-complete">
      <Icons.Trophy className="h-5 w-5 text-slate-600 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-sm font-medium text-slate-800">
          Strategic Review Cycle Complete
          {review_year && <span className="ml-2 text-xs text-slate-500 font-normal">({review_year})</span>}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          This review cycle has been formally closed and preserved as a historical record.
          {cycle_completion_basis === "legacy_allison_delivered" && (
            <span className="ml-1 text-amber-600 font-medium"> Completed via Legacy Allison Delivery.</span>
          )}
        </p>
        {/* WS1 — Phase 5A: compact read receipt (shown when client viewed before cycle was closed) */}
        {readReceipt !== null && readReceipt.first_viewed_at && (
          <p className="text-xs mt-1.5 flex items-center gap-1.5 text-slate-500" data-testid="str-read-receipt">
            <Icons.Eye className="h-3 w-3 shrink-0" />
            First viewed {fmtViewDate(readReceipt.first_viewed_at)}
            {readReceipt.last_viewed_at && readReceipt.last_viewed_at !== readReceipt.first_viewed_at && (
              <span className="ml-2">· Last viewed {fmtViewDate(readReceipt.last_viewed_at)}</span>
            )}
          </p>
        )}
      </div>
      <a href="/sales/estimates?create=1&prefill_service=ADV-STRATEGY"
         className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-slate-700 whitespace-nowrap transition-colors mt-0.5 shrink-0"
         data-testid="str-start-new-cycle-btn">
        <Icons.Plus className="h-3 w-3" />
        Start New Review Cycle
      </a>
    </div>
  );

  if (state === "review_underway") return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-3" data-testid="str-state-review-underway">
      <Icons.Brain className="h-5 w-5 text-slate-500 shrink-0" />
      <p className="text-sm font-medium text-slate-700">{current_state_label || "Strategic Review underway"} — view the Allison Findings tab for results</p>
    </div>
  );

  return null;
}

// ── STR Cycle Selector (Increment 3) ─────────────────────────────────────────
// ── Phase 5B: Compact cycle history timeline ─────────────────────────────────────
// Surfaces the published/viewed/closed milestones that are already persisted for each
// STR cycle. Shown when at least one cycle has a published milestone (published_at or
// first_viewed_at). Hidden while every cycle is still pre-publication.
function StrCycleTimeline({ cycles, selectedEngagementId }) {
  if (!cycles || cycles.length === 0) return null;

  // Resolve which cycle is currently selected — mirrors StrCycleSelector logic
  const resolveCurrent = (c, idx) =>
    c.engagement_id === selectedEngagementId ||
    (!selectedEngagementId && !c.is_legacy_unlinked && idx === 0);

  // Only render if at least one cycle has a delivery/view milestone
  const hasAnyMilestone = cycles.some(c => c.published_at || c.first_viewed_at);
  if (!hasAnyMilestone) return null;

  const fmtMs = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        month: "short", day: "numeric", year: "numeric",
      });
    } catch { return null; }
  };

  return (
    <div className="mb-4 space-y-1.5" data-testid="str-cycle-timeline">
      {cycles.map((cycle, idx) => {
        const isCurrent = resolveCurrent(cycle, idx);

        // Build the ordered milestone list — skip absent or duplicate entries
        const rawMilestones = [
          { label: "Started",      date: cycle.created_at },
          { label: "Published",    date: cycle.published_at },
          { label: "First viewed", date: cycle.first_viewed_at },
          {
            label: "Last viewed",
            date: cycle.last_viewed_at && cycle.last_viewed_at !== cycle.first_viewed_at
              ? cycle.last_viewed_at : null,
          },
          { label: "Closed",       date: cycle.cycle_completed_at },
        ].filter(m => m.date);

        // Hide a cycle that has no milestones worth displaying
        if (rawMilestones.length === 0) return null;

        return (
          <div
            key={cycle.engagement_id || "legacy"}
            className={`rounded-lg px-3 py-2 transition-opacity
              ${isCurrent
                ? "bg-slate-50 border border-slate-200"
                : "border border-transparent opacity-55 hover:opacity-75"}`}
            data-testid={`str-cycle-timeline-${cycle.engagement_id || "legacy"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[11px] font-semibold tracking-tight
                ${isCurrent ? "text-slate-800" : "text-slate-500"}`}>
                {cycle.cycle_label}
              </span>
              {isCurrent && (
                <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-600">
                  Current
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              {rawMilestones.map((m, mi) => (
                <span key={m.label} className="flex items-center gap-1">
                  {mi > 0 && (
                    <span className={isCurrent ? "text-slate-300" : "text-slate-200"}>·</span>
                  )}
                  <span className={`text-[10px] ${isCurrent ? "text-slate-500" : "text-slate-400"}`}>
                    {m.label}
                  </span>
                  <span className={`text-[10px] font-medium ${isCurrent ? "text-slate-700" : "text-slate-400"}`}>
                    {fmtMs(m.date)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Compact cycle switcher shown when a client has more than one STR cycle.
// Selecting a historical cycle loads that cycle's records in read-only mode.
function StrCycleSelector({ availableCycles, selectedEngagementId, onSelect }) {
  if (!availableCycles || availableCycles.length <= 1) return null;

  const STATE_BADGE: Record<string, string> = {
    cycle_complete:          "bg-emerald-100 text-emerald-800",
    review_complete:         "bg-emerald-100 text-emerald-800",
    management_review:       "bg-violet-100 text-violet-800",
    analysis_complete:       "bg-teal-100 text-teal-800",
    analysis_running:        "bg-blue-100 text-blue-800",
    questionnaire_submitted: "bg-orange-100 text-orange-800",
    questionnaire_published: "bg-violet-100 text-violet-800",
    assignment_pending:      "bg-blue-100 text-blue-800",
    review_ready:            "bg-emerald-100 text-emerald-800",
  };

  const STATE_LABEL: Record<string, string> = {
    cycle_complete: "Complete", review_complete: "Delivered",
    management_review: "Mgmt Review", analysis_complete: "Analysis Done",
    analysis_running: "Analysing", questionnaire_submitted: "Submitted",
    questionnaire_published: "Published", assignment_pending: "Draft",
    review_ready: "Ready",
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="str-cycle-selector">
      <span className="text-xs text-slate-500 font-medium shrink-0">Cycle:</span>
      {availableCycles.map((cycle) => {
        const isSelected = cycle.engagement_id === selectedEngagementId
          || (!selectedEngagementId && !cycle.is_legacy_unlinked && availableCycles.indexOf(cycle) === 0);
        const badge = STATE_BADGE[cycle.display_state] || "bg-slate-100 text-slate-700";
        const label = STATE_LABEL[cycle.display_state] || cycle.display_state;
        return (
          <button key={cycle.engagement_id || "legacy"}
            onClick={() => onSelect(cycle.engagement_id)}
            data-testid={`str-cycle-btn-${cycle.engagement_id || "legacy"}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-all
              ${isSelected
                ? "border-slate-700 bg-slate-800 text-white shadow-sm"
                : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
              }`}>
            {cycle.cycle_label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isSelected ? "bg-white/20 text-white" : badge}`}>
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function LibraryBrowser({ templates, modules, questions, docReqs }) {
  const groups = useMemo(() => {
    const g = {};
    modules.forEach((m) => { (g[m.module_type] = g[m.module_type] || []).push(m); });
    return g;
  }, [modules]);
  const TYPE_LABELS = { universal_core: "Universal", residence: "Residence", jurisdiction: "Jurisdictions", subject: "Topics", treaty: "Treaty Interactions", staff_added: "Staff-Added" };

  return (
    <Tabs defaultValue="modules">
      <TabsList data-testid="sr-lib-tabs">
        <TabsTrigger value="questionnaires" data-testid="sr-lib-questionnaires">Questionnaires</TabsTrigger>
        <TabsTrigger value="modules" data-testid="sr-lib-modules">Modules</TabsTrigger>
        <TabsTrigger value="questions" data-testid="sr-lib-questions">Questions</TabsTrigger>
        <TabsTrigger value="documents" data-testid="sr-lib-documents">Document Requirements</TabsTrigger>
      </TabsList>

      <TabsContent value="questionnaires" className="mt-4 space-y-3">
        {templates.map((t) => (
          <SectionCard key={t.code} title={t.name} icon="ClipboardList">
            <div className="text-xs text-slate-500 space-y-1">
              <div><span className="font-mono">{t.code}</span> · v{t.version} · {t.client_type}</div>
              <div><strong>Required:</strong> {(t.required_modules || []).join(", ") || "—"}</div>
              <div><strong>Default:</strong> {(t.default_modules || []).join(", ") || "—"}</div>
              <div><strong>Optional:</strong> {(t.optional_modules || []).join(", ") || "—"}</div>
            </div>
          </SectionCard>
        ))}
        <AlertBanner tone="info" title="More questionnaires coming">Business Owner, US Expat, and Estate Planning reviews will appear here as they are published.</AlertBanner>
      </TabsContent>

      <TabsContent value="modules" className="mt-4 space-y-5" data-testid="sr-lib-modules-content">
        {Object.keys(groups).map((type) => (
          <div key={type}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{TYPE_LABELS[type] || type}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groups[type].map((m) => <ModuleCard key={m.code} m={m} />)}
            </div>
          </div>
        ))}
      </TabsContent>

      <TabsContent value="questions" className="mt-4" data-testid="sr-lib-questions-content">
        <QuestionTable questions={questions} />
      </TabsContent>

      <TabsContent value="documents" className="mt-4 space-y-3" data-testid="sr-lib-documents-content">
        {docReqs.length === 0 ? <EmptyState icon="Paperclip" title="No document requirements" /> : docReqs.map((d) => (
          <SectionCard key={d.document_type} title={d.title} icon="Paperclip"
            action={<Pill tone={d.required ? "danger" : "neutral"}>{d.required ? "Required" : "Conditional"}</Pill>}>
            <div className="text-xs text-slate-500 space-y-1">
              <div><span className="font-mono">{d.document_type}</span></div>
              <div><strong>Requested by modules:</strong> {d.modules.join(", ")}</div>
              <div><strong>Related questions:</strong> {d.questions.map((q) => q.text).join(" · ")}</div>
            </div>
          </SectionCard>
        ))}
      </TabsContent>
    </Tabs>
  );
}

function ModuleCard({ m }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl p-4" data-testid={`sr-module-${m.code}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-slate-800 text-sm">{m.name}</div>
          <div className="text-[11px] text-slate-400 font-mono">{m.code} · v{m.version}</div>
        </div>
        <Pill tone={MODULE_TONES[m.module_type] || "neutral"}>{m.module_type}</Pill>
      </div>
      {m.description && <p className="text-xs text-slate-500 mt-2">{m.description}</p>}
      <div className="flex flex-wrap gap-2 mt-3 text-[11px] text-slate-500">
        <Badge variant="secondary">{m.question_count} questions</Badge>
        {(m.required_documents?.length > 0) && <Badge variant="outline"><Icons.Paperclip className="h-3 w-3 mr-1" />{m.required_documents.length} docs</Badge>}
        {(m.ptf_mappings?.length > 0) && <Badge variant="outline">{m.ptf_mappings.length} PTF</Badge>}
        {(m.applicable_jurisdictions?.length > 0) && <Badge variant="outline">{m.applicable_jurisdictions.join(", ")}</Badge>}
      </div>
      <button onClick={() => setOpen((o) => !o)} className="text-[11px] text-blue-600 hover:underline mt-2" data-testid={`sr-module-toggle-${m.code}`}>
        {open ? "Hide" : "Show"} trigger rules
      </button>
      {open && (
        <pre className="mt-2 bg-slate-50 rounded-lg p-2 text-[10px] text-slate-600 overflow-auto">{JSON.stringify(m.trigger_rules, null, 2) || "always included"}</pre>
      )}
    </div>
  );
}

function QuestionTable({ questions }) {
  const [q, setQ] = useState("");
  const filtered = questions.filter((x) => !q || (x.text + x.code + (x.section || "")).toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…" className="mb-3 max-w-sm" data-testid="sr-question-search" />
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400">
            <tr><th className="text-left px-3 py-2">Question</th><th className="text-left px-3 py-2">Section</th><th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">Modules</th><th className="text-left px-3 py-2">v</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((x) => (
              <tr key={x.code} data-testid={`sr-question-${x.code}`}>
                <td className="px-3 py-2 text-slate-800">{x.text}
                  <div className="flex gap-1 mt-0.5">
                    {x.required && <Badge variant="destructive" className="text-[9px]">required</Badge>}
                    {x.doc_request && <Badge variant="outline" className="text-[9px]">doc</Badge>}
                    {x.ptf_mapping && <Badge variant="secondary" className="text-[9px]">PTF: {x.ptf_mapping}</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2 text-slate-500">{x.section}</td>
                <td className="px-3 py-2 text-slate-500">{x.response_type}</td>
                <td className="px-3 py-2 text-[11px] text-slate-500">{(x.modules || []).join(", ")}</td>
                <td className="px-3 py-2 text-slate-400">{x.version}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const CAT_META = {
  missing_information: { label: "Missing Information", tone: "warning", icon: "HelpCircle" },
  missing_documentation: { label: "Missing Documentation", tone: "warning", icon: "FileWarning" },
  ptf_candidate: { label: "Permanent Tax File Candidates", tone: "info", icon: "Landmark" },
  staff_decision: { label: "Staff Decision Required", tone: "danger", icon: "GitPullRequestArrow" },
  report_observation: { label: "Report Observations", tone: "purple", icon: "FileText" },
  internal_observation: { label: "Internal Observations", tone: "neutral", icon: "Eye" },
  info_already_available: { label: "Already Answered (No Follow-up)", tone: "success", icon: "CheckCircle2" },
};
const SEV_TONE = { critical: "danger", high: "warning", medium: "info", low: "neutral", informational: "neutral" };
const CAT_ORDER = ["staff_decision", "missing_information", "missing_documentation", "ptf_candidate", "report_observation", "internal_observation", "info_already_available"];

// Phase 1D: action-owner semantics per resolution_type
const RT_META = {
  staff_input:           { badge: "ACTION REQUIRED · STAFF",  color: "bg-rose-50 border-rose-200 text-rose-700",   cta: "Provide Staff Input",       ctaIcon: "MessageSquarePlus" },
  staff_decision:        { badge: "ACTION REQUIRED · STAFF",  color: "bg-rose-50 border-rose-200 text-rose-700",   cta: "Record Decision",           ctaIcon: "CheckSquare" },
  client_question:       { badge: "ACTION REQUIRED · CLIENT", color: "bg-amber-50 border-amber-200 text-amber-700", cta: "Request From Client",       ctaIcon: "SendHorizonal" },
  client_missing_answer: { badge: "ACTION REQUIRED · CLIENT", color: "bg-amber-50 border-amber-200 text-amber-700", cta: "Return Client to Question", ctaIcon: "CornerDownRight" },
  client_document:       { badge: "ACTION REQUIRED · CLIENT DOCUMENT", color: "bg-orange-50 border-orange-200 text-orange-700", cta: "Create Document Request", ctaIcon: "Paperclip" },
  review_only:           { badge: "REVIEW REQUIRED",          color: "bg-slate-50 border-slate-200 text-slate-600",  cta: "Mark Reviewed",            ctaIcon: "Eye" },
};

// P0: operational lifecycle state metadata — overrides RT_META badge when lifecycle has progressed
// past the initial "needs action" state. active=true keeps the card at normal opacity.
const LIFECYCLE_META = {
  notify_failed:       { badge: "CLIENT NOTIFICATION FAILED",       color: "bg-rose-50 border-rose-200 text-rose-700",        active: true },
  needs_action_staff:  { badge: "ACTION REQUIRED · STAFF",           color: "bg-rose-50 border-rose-200 text-rose-700",        active: true },
  needs_action_client: { badge: "ACTION REQUIRED · CLIENT",          color: "bg-amber-50 border-amber-200 text-amber-700",     active: true },
  needs_action_doc:    { badge: "ACTION REQUIRED · CLIENT DOCUMENT", color: "bg-orange-50 border-orange-200 text-orange-700",  active: true },
  staff_responded:     { badge: "STAFF RESPONSE RECORDED",           color: "bg-emerald-50 border-emerald-200 text-emerald-700", active: true },
  awaiting_client:     { badge: "AWAITING CLIENT",                   color: "bg-amber-50 border-amber-200 text-amber-700",     active: true },
  awaiting_doc:        { badge: "AWAITING CLIENT DOCUMENT",          color: "bg-orange-50 border-orange-200 text-orange-700",  active: true },
  client_responded:    { badge: "CLIENT RESPONDED",                  color: "bg-teal-50 border-teal-200 text-teal-700",        active: true },
  doc_received:        { badge: "DOCUMENT RECEIVED",                 color: "bg-blue-50 border-blue-200 text-blue-700",        active: true },
  doc_not_available:   { badge: "DOCUMENT NOT AVAILABLE",            color: "bg-slate-50 border-slate-200 text-slate-600",     active: true },
  reanalyzing:         { badge: "ALLISON RE-EVALUATING",             color: "bg-violet-50 border-violet-200 text-violet-700",  active: true },
  resolved:            { badge: "RESOLVED",                          color: "bg-slate-50 border-slate-200 text-slate-400",     active: false },
  dismissed:           { badge: "DISMISSED",                         color: "bg-slate-50 border-slate-200 text-slate-400",     active: false },
  review_only:         { badge: null, color: null, active: false },
};

// Inline expandable staff response panel (staff_input / staff_decision / answer-known for client_question)
function StaffResponsePanel({ finding, completionId, onSaved, label: labelProp }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [decision, setDecision] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const sr = finding.staff_response;
  if (sr?.text && !open) {
    return (
      <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs" data-testid={`sr-staff-response-${finding.id}`}>
        <div className="flex items-center justify-between">
          <span className="font-semibold text-emerald-700 flex items-center gap-1"><Icons.CheckCircle2 className="h-3.5 w-3.5" />Staff Response recorded</span>
          <button onClick={() => { setText(sr.text); setDecision(sr.decision_value || ""); setOpen(true); }}
            className="text-emerald-600 underline text-[11px]" data-testid={`sr-edit-response-${finding.id}`}>Edit</button>
        </div>
        <p className="text-slate-700 mt-1">{sr.text}</p>
        {sr.decision_value && <p className="text-emerald-700 mt-0.5"><span className="font-medium">Decision: </span>{sr.decision_value}</p>}
        <p className="text-slate-400 mt-0.5 text-[10px]">by {sr.responder_name} · {sr.responded_at ? new Date(sr.responded_at).toLocaleString() : "—"}</p>
        <p className="text-emerald-600 text-[10px] mt-0.5 flex items-center gap-0.5"><Icons.Zap className="h-2.5 w-2.5" />Included in Allison's next analysis</p>
      </div>
    );
  }

  const rt = finding.resolution_type || "staff_decision";
  // P0: labelProp allows callers to customise the button label (e.g. "Answer Known — Staff Has This")
  const label = labelProp || (rt === "staff_input" ? "Provide Staff Input" : "Record Decision");

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-rose-700 border border-rose-200 bg-rose-50 rounded-lg px-3 py-1.5 hover:bg-rose-100 transition-colors"
        data-testid={`sr-respond-btn-${finding.id}`}>
        <Icons.MessageSquarePlus className="h-3.5 w-3.5" />{label}
      </button>
    );
  }

  const save = async () => {
    if (!text.trim()) { toast.error("Response text is required"); return; }
    setSaving(true);
    try {
      await api.post(`/strategic-reviews/completion/${completionId}/findings/${finding.id}/respond`,
        { text: text.trim(), decision_value: decision.trim() || undefined, notes: notes.trim() || undefined });
      toast.success("Response saved — Allison will include it in the next analysis");
      setOpen(false); setText(""); setDecision(""); setNotes("");
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save response"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 w-full border border-rose-200 rounded-lg bg-rose-50 p-3" data-testid={`sr-response-panel-${finding.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-rose-700">{label}</span>
        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><Icons.X className="h-3.5 w-3.5" /></button>
      </div>
      <p className="text-xs text-slate-600 mb-2 italic">"{finding.summary}"</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Provide your response or input…"
        className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-rose-300"
        data-testid={`sr-response-text-${finding.id}`} />
      {rt === "staff_decision" && (
        <input value={decision} onChange={(e) => setDecision(e.target.value)} placeholder="Decision value (optional, e.g. 'advisory-only')"
          className="w-full mt-1.5 border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none"
          data-testid={`sr-decision-value-${finding.id}`} />
      )}
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes (optional)"
        className="w-full mt-1.5 border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none"
        data-testid={`sr-response-notes-${finding.id}`} />
      <div className="flex justify-end gap-2 mt-2">
        <button onClick={() => setOpen(false)} className="text-xs text-slate-500 px-2 py-1">Cancel</button>
        <button onClick={save} disabled={saving}
          className="text-xs bg-rose-600 text-white px-3 py-1.5 rounded-lg hover:bg-rose-700 disabled:opacity-50 flex items-center gap-1"
          data-testid={`sr-save-response-${finding.id}`}>
          {saving ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Zap className="h-3 w-3" />}
          Save &amp; Re-evaluate
        </button>
      </div>
    </div>
  );
}

// P0: derive the operational lifecycle state for a finding card.
// Returns a phase string and optional detail data used to render the banner and badge.
// Precedence: Allison Re-evaluating > Staff Responded > Client Responded > Document state > Awaiting > Needs Action.
function deriveLifecycleState(f, completion, runStatus) {
  const _CAT_TO_RT = { staff_decision: "staff_decision", missing_information: "client_question",
                       missing_documentation: "client_document" };
  const rt = f.resolution_type || _CAT_TO_RT[f.category] || "review_only";
  const sr = f.staff_response;
  const followUpRef = f.follow_up_ref;
  const docReqStatus = f.document_request_status;

  if (f.disposition === "dismissed") return { phase: "dismissed" };

  // Allison is re-evaluating — highest priority for findings with existing evidence
  if (runStatus === "running" && (sr?.text || followUpRef || docReqStatus)) {
    return { phase: "reanalyzing" };
  }

  if (rt === "staff_decision" || rt === "staff_input") {
    if (sr?.text) return { phase: "staff_responded", detail: sr.responded_at, responder: sr.responder_name };
    return { phase: "needs_action_staff" };
  }

  if (rt === "client_question") {
    if (sr?.text) return { phase: "staff_responded", detail: sr.responded_at, responder: sr.responder_name };
    if (followUpRef) {
      const rounds = (completion?.supplemental_rounds) || [];
      const round = rounds.find((r) => r.round === followUpRef.round);
      if (round?.status === "submitted") {
        return { phase: "client_responded", detail: round.submitted_at, round: followUpRef.round };
      }
      // Notification failed: client was not reached — do NOT show Awaiting Client
      if (round?.notification_status === "failed") {
        return { phase: "notify_failed", round: followUpRef.round };
      }
      return { phase: "awaiting_client", detail: round?.opened_at, round: followUpRef.round,
               notification: round?.notification_status, notificationSentAt: round?.notification_sent_at };
    }
    return { phase: "needs_action_client" };
  }

  if (rt === "client_document") {
    if (f.document_request_id) {
      if (docReqStatus === "awaiting_review" || docReqStatus === "uploaded") {
        return { phase: "doc_received", detail: f.document_request_updated_at };
      }
      if (docReqStatus === "not_available") {
        return { phase: "doc_not_available", detail: f.document_request_updated_at };
      }
      if (docReqStatus === "approved") {
        return { phase: "resolved", detail: f.document_request_updated_at };
      }
      // missing, requested, needs_revision, or null (safe fallback)
      return { phase: "awaiting_doc", detail: f.document_request_updated_at };
    }
    return { phase: "needs_action_doc" };
  }

  if (rt === "client_missing_answer") {
    if (followUpRef) {
      const rounds = (completion?.supplemental_rounds) || [];
      const round = rounds.find((r) => r.round === followUpRef.round);
      if (round?.status === "submitted") {
        return { phase: "client_responded", detail: round.submitted_at, round: followUpRef.round };
      }
      if (round?.notification_status === "failed") {
        return { phase: "notify_failed", round: followUpRef.round };
      }
      return { phase: "awaiting_client", detail: round?.opened_at, round: followUpRef.round,
               notification: round?.notification_status };
    }
    return { phase: "needs_action_client" };
  }

  if (f.disposition === "completed") return { phase: "resolved" };
  return { phase: "review_only" };
}

function FindingCard({ f, completionId, onDispositionChange, onAddToPackage, onLoad, completion, runStatus }) {
  // P0 Fix A: frontend fallback mirrors backend _derive_resolution_type() so legacy findings
  // (resolution_type=null, created before Phase 1D) are correctly classified rather than
  // silently falling to review_only for all categories except staff_decision.
  const _CAT_TO_RT = { staff_decision: "staff_decision", missing_information: "client_question",
                       missing_documentation: "client_document" };
  const rt = f.resolution_type || _CAT_TO_RT[f.category] || "review_only";
  const rtm = RT_META[rt] || RT_META.review_only;

  // P0: derive operational lifecycle state from canonical fields
  const lifecycleState = deriveLifecycleState(f, completion, runStatus);
  const lsMeta = LIFECYCLE_META[lifecycleState.phase] || { badge: rtm.badge, color: rtm.color, active: true };

  const [showDisp, setShowDisp] = useState(false);
  const [dispNotes, setDispNotes] = useState(f.disposition_notes || "");
  const [savingDisp, setSavingDisp] = useState(false);

  const saveDisp = async (disp) => {
    setSavingDisp(true);
    try {
      await api.post(`/strategic-reviews/completion/${completionId}/findings/${f.id}/disposition`,
        { disposition: disp, notes: dispNotes.trim() || undefined });
      onDispositionChange?.(f.id, disp, dispNotes);
      toast.success("Disposition updated");
      setShowDisp(false);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Could not update";
      toast.error(msg);
    } finally { setSavingDisp(false); }
  };

  const needsResponse = ["staff_input", "staff_decision", "client_question", "client_missing_answer", "client_document"].includes(rt);
  const hasResponse = needsResponse && (f.staff_response?.text || f.document_request_id || f.disposition === "completed");

  // P0: dim only when truly terminal — dismissed, or completed with no active lifecycle.
  // Active lifecycle (Awaiting Client, Client Responded, etc.) keeps card at normal opacity
  // so staff can see what's happening even though approve_package set disposition="completed".
  const isActiveLc = lsMeta?.active === true;
  const isDimmed = f.disposition === "dismissed" || (f.disposition === "completed" && !isActiveLc);

  // Show action CTAs only when staff still needs to act or has a response to display/edit.
  const _WAITING = new Set(["awaiting_client", "awaiting_doc", "client_responded", "doc_received",
                             "doc_not_available", "reanalyzing", "resolved", "dismissed", "notify_failed"]);
  const showActions = !isDimmed && !_WAITING.has(lifecycleState.phase);

  // Lifecycle banner phases (excludes needs_action_* — badge-only; staff_responded — handled by StaffResponsePanel)
  const _BANNER = new Set(["awaiting_client", "awaiting_doc", "client_responded", "doc_received",
                            "doc_not_available", "reanalyzing", "resolved", "dismissed", "notify_failed"]);

  return (
    <div className={`border rounded-xl p-4 transition-opacity ${isDimmed ? "opacity-60" : ""} ${f.blocks_report && !f.blocks_report_override && !isDimmed ? "border-rose-200 bg-rose-50/30" : "border-slate-200 bg-white"}`}
      data-testid={`sr-finding-${f.id}`}>
      <div className="flex items-start gap-2">
        {/* Severity + lifecycle state badge */}
        <div className="flex flex-col gap-1 shrink-0">
          <Pill tone={SEV_TONE[f.severity] || "neutral"}>{f.severity}</Pill>
          {lsMeta?.badge && (
            <span className={`inline-flex items-center text-[9px] font-semibold px-1.5 py-0.5 rounded border ${lsMeta.color}`}
              data-testid={`sr-rt-badge-${f.id}`}>{lsMeta.badge}</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-800 text-sm">{f.summary}</div>
          {f.details && <p className="text-xs text-slate-600 mt-1">{f.details}</p>}
          {f.reasoning && <p className="text-xs text-slate-500 mt-1"><span className="font-medium">Why: </span>{f.reasoning}</p>}
          {f.authority && <p className="text-[11px] text-slate-400 mt-1">Authority: {f.authority}</p>}
          {f.recommended_action && <p className="text-xs text-blue-700 mt-1">→ {f.recommended_action}</p>}
          {f.category === "missing_information" && f.suggested_supplemental_question && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              <span className="font-medium">Draft question: </span>{f.suggested_supplemental_question}
            </div>
          )}
          {f.category === "ptf_candidate" && f.ptf_candidate && (
            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
              <span className="font-medium">Proposed fact: </span>{f.ptf_candidate.field} = {String(f.ptf_candidate.value)}
            </div>
          )}
          {f.blocks_report_override && (
            <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1">
              <Icons.AlertTriangle className="h-3 w-3 text-amber-500" />
              Report block overridden by {f.blocks_report_override.overridden_by_name}: "{f.blocks_report_override.reason}"
            </div>
          )}
          {f.supported_by?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide mr-1">Supported by:</span>
              {f.supported_by.map((s) => <Badge key={s} variant="secondary" className="text-[9px]"><Icons.Check className="h-2.5 w-2.5 mr-0.5 text-emerald-600" />{s}</Badge>)}
            </div>
          )}

          {/* P0: Operational lifecycle state banner — shows post-action status */}
          {_BANNER.has(lifecycleState.phase) && lsMeta?.badge && (
            <div className={`mt-2 flex flex-wrap items-center gap-2 text-xs px-3 py-2 rounded-lg border ${lsMeta.color}`}
              data-testid={`sr-lifecycle-banner-${f.id}`}>
              {lifecycleState.phase === "reanalyzing"
                ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                : (lifecycleState.phase === "awaiting_client" || lifecycleState.phase === "awaiting_doc")
                ? <Icons.Clock className="h-3.5 w-3.5 shrink-0" />
                : (lifecycleState.phase === "client_responded" || lifecycleState.phase === "doc_received")
                ? <Icons.MessageSquare className="h-3.5 w-3.5 shrink-0" />
                : lifecycleState.phase === "doc_not_available"
                ? <Icons.FileX className="h-3.5 w-3.5 shrink-0" />
                : lifecycleState.phase === "notify_failed"
                ? <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                : <Icons.CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
              <span className="font-semibold">{lsMeta.badge}</span>
              {lifecycleState.detail && (
                <span className="opacity-70">· {new Date(lifecycleState.detail).toLocaleDateString()}</span>
              )}
              {lifecycleState.round && <span className="opacity-60">Round {lifecycleState.round}</span>}
              {lifecycleState.responder && <span className="opacity-60">by {lifecycleState.responder}</span>}
              {lifecycleState.phase === "awaiting_client" && lifecycleState.notification && (
                <span className={`ml-auto text-[10px] font-medium ${lifecycleState.notification === "sent" ? "text-emerald-600" : "text-rose-600"}`}
                  data-testid={`sr-notification-status-${f.id}`}>
                  {lifecycleState.notification === "sent" ? "Client notified" : "Notification failed"}
                </span>
              )}
            </div>
          )}

          {/* === Phase 1D / P0 ACTION AREA ===
              P0 Fix: flex-col so StaffResponsePanel always expands to full card width.
              Action area hidden while waiting for client/reanalyzing — no staff action needed. */}
          {showActions && (
            <div className="mt-3 flex flex-col gap-2" data-testid={`sr-action-area-${f.id}`}>
              {/* Staff response panel for staff_input / staff_decision */}
              {(rt === "staff_input" || rt === "staff_decision") && (
                <StaffResponsePanel finding={f} completionId={completionId} onSaved={onLoad} />
              )}

              {/* P0 dual-path for client_question:
                  – Answer Known: staff has the factual answer → record it (form renders first/above)
                  – Request From Client: info must come from client → add to package for outreach */}
              {rt === "client_question" && !f.staff_response?.text && (
                <>
                  <StaffResponsePanel finding={f} completionId={completionId} onSaved={onLoad}
                    label="Answer Known — Staff Has This" />
                  <button onClick={() => onAddToPackage?.(f)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 border border-amber-200 bg-amber-50 rounded-lg px-3 py-1.5 hover:bg-amber-100 transition-colors self-start"
                    data-testid={`sr-request-from-client-${f.id}`}>
                    <Icons.SendHorizonal className="h-3.5 w-3.5" />Request From Client
                  </button>
                </>
              )}
              {/* After staff records an answer to a client_question, show recorded response for editing */}
              {rt === "client_question" && f.staff_response?.text && (
                <StaffResponsePanel finding={f} completionId={completionId} onSaved={onLoad}
                  label="Answer Known — Staff Has This" />
              )}

              {rt === "client_missing_answer" && (
                <button onClick={() => onAddToPackage?.(f)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 border border-amber-200 bg-amber-50 rounded-lg px-3 py-1.5 hover:bg-amber-100 transition-colors self-start"
                  data-testid={`sr-add-followup-${f.id}`}>
                  <Icons.CornerDownRight className="h-3.5 w-3.5" />Return Client to Question
                </button>
              )}
              {/* Create Document Request CTA — hidden once document_request_id is written by approve_package */}
              {rt === "client_document" && !f.document_request_id && (
                <button onClick={() => onAddToPackage?.(f)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-700 border border-orange-200 bg-orange-50 rounded-lg px-3 py-1.5 hover:bg-orange-100 transition-colors self-start"
                  data-testid={`sr-create-doc-req-${f.id}`}>
                  <Icons.Paperclip className="h-3.5 w-3.5" />Create Document Request
                </button>
              )}
              {rt === "review_only" && (
                <button onClick={() => saveDisp("completed")}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 bg-slate-50 rounded-lg px-3 py-1.5 hover:bg-slate-100 transition-colors self-start"
                  data-testid={`sr-mark-reviewed-${f.id}`}>
                  <Icons.Eye className="h-3.5 w-3.5" />Mark Reviewed
                </button>
              )}
            </div>
          )}

          {/* Disposition secondary control (always available) */}
          <div className="mt-3 border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-slate-400 uppercase tracking-wide">Disposition:</span>
              <select value={f.disposition || "pending"}
                onChange={(e) => { setShowDisp(true); saveDisp(e.target.value); }}
                className="text-[11px] border border-slate-200 rounded-lg px-1.5 py-1 bg-white"
                data-testid={`sr-disposition-${f.id}`}>
                {["pending", "approved", "dismissed", "needs_discussion", "escalated", "completed"].map((d) => (
                  <option key={d} value={d}>{d.replace("_", " ")}</option>
                ))}
              </select>
              {f.disposition_notes && (
                <span className="text-[11px] text-slate-500 italic">"{f.disposition_notes}"</span>
              )}
              {needsResponse && !hasResponse && f.disposition === "approved" && (
                <span className="text-[11px] text-rose-600 flex items-center gap-0.5">
                  <Icons.AlertCircle className="h-3 w-3" />Approved without evidence
                </span>
              )}
            </div>
            {(showDisp || ["dismissed", "needs_discussion"].includes(f.disposition)) && (
              <textarea value={dispNotes} onChange={(e) => setDispNotes(e.target.value)}
                placeholder="Disposition notes (optional)" rows={2}
                className="w-full mt-2 border border-slate-200 rounded-lg px-2 py-1 text-xs bg-white focus:outline-none"
                data-testid={`sr-disposition-notes-${f.id}`} />
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 shrink-0">
          <Badge variant="outline" className="text-[10px]">{f.confidence}%</Badge>
          {f.blocks_report && !f.blocks_report_override && !isDimmed && (
            <span className="text-[9px] bg-rose-100 text-rose-600 px-1.5 py-0.5 rounded font-medium">Blocks Report</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// P0 VIEWER: StrSubmissionViewer
// Read-only viewer for the client's submitted STR Organizer answers.
// Uses the existing GET /assignments/{aid}/completion endpoint which returns:
//   completion.submitted_snapshot_state — immutable state at submission time
//   state — recomputed state (fallback if snapshot missing)
//   document_requests — doc requests linked to this assignment
// ─────────────────────────────────────────────────────────────────────────────

function ReadOnlyAnswerDisplay({ q, docReqs }) {
  const isDna = q.answer_status === "does_not_apply";
  const isAnswered = q.answered;
  const val = q.value;
  const rt = q.response_type;

  // Find document requests linked to this question
  const linkedDocs = (docReqs || []).filter(
    (d) => (d.origin?.question_code === q.code) && d.request_status !== "not_applicable"
  );

  const renderValue = () => {
    if (isDna) return <span className="text-slate-400 italic text-sm px-2 py-1">Does not apply</span>;
    if (!isAnswered || val === null || val === undefined || val === "")
      return <span className="text-slate-300 italic text-sm">Not answered</span>;

    if (rt === "yes_no" || rt === "yes_no_unknown") {
      const COLOR = { yes: "bg-emerald-100 text-emerald-800", no: "bg-rose-100 text-rose-700", unknown: "bg-slate-100 text-slate-600" };
      return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${COLOR[val] || "bg-slate-100 text-slate-700"}`}>{val}</span>;
    }
    if (rt === "multi_select" && Array.isArray(val)) {
      return <div className="flex flex-wrap gap-1.5">{val.map((v, i) => <span key={i} className="px-2.5 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700 border border-slate-200">{v}</span>)}</div>;
    }
    if (rt === "repeating_record" && Array.isArray(val)) {
      if (val.length === 0) return <span className="text-slate-300 italic text-sm">No entries</span>;
      return (
        <div className="space-y-2">
          {val.map((rec, i) => (
            <div key={i} className="border border-slate-100 rounded-xl p-3 bg-slate-50 text-xs text-slate-600 space-y-1">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Entry {i + 1}</span>
              {Object.entries(rec).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                <div key={k} className="flex gap-1.5"><span className="text-slate-400 shrink-0">{k.replace(/_/g, " ")}:</span><span className="text-slate-700">{String(v)}</span></div>
              ))}
            </div>
          ))}
        </div>
      );
    }
    if (rt === "date" && val) {
      try { return <span className="text-sm text-slate-700">{new Date(val).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>; }
      catch { return <span className="text-sm text-slate-700">{val}</span>; }
    }
    if (rt === "document_request") {
      // Value is not an inline upload — handled by linked docs below
      return null;
    }
    return <span className="text-sm text-slate-700 whitespace-pre-wrap">{String(val)}</span>;
  };

  const docStatusColor = { uploaded: "text-emerald-700 bg-emerald-50 border-emerald-200", awaiting_review: "text-emerald-600 bg-emerald-50 border-emerald-200", approved: "text-emerald-700 bg-emerald-50 border-emerald-200", missing: "text-amber-700 bg-amber-50 border-amber-200", received: "text-blue-700 bg-blue-50 border-blue-200" };
  const docLabel = { uploaded: "Uploaded", awaiting_review: "Uploaded", approved: "Approved", missing: "Missing", received: "Received" };

  return (
    <div data-testid={`str-sub-q-${q.code}`}>
      <label className="block text-sm font-medium text-slate-800 mb-1">
        {q.text}{q.required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {q.help_text && <p className="text-xs text-slate-400 mb-1.5">{q.help_text}</p>}
      <div className="mt-1">{renderValue()}</div>
      {linkedDocs.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {linkedDocs.map((d) => (
            <div key={d.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${docStatusColor[d.status] || "bg-slate-50 border-slate-200 text-slate-600"}`}
              data-testid={`str-sub-doc-${d.id}`}>
              <div className="flex items-center gap-1.5 min-w-0">
                <Icons.Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate font-medium">{d.title}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-semibold">{docLabel[d.status] || d.status}</span>
                {d.linked_document_id && (
                  <a href={`/api/documents/${d.linked_document_id}/content`} target="_blank" rel="noopener noreferrer"
                    className="underline hover:no-underline" data-testid={`str-sub-doc-open-${d.id}`}>View</a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StrSubmissionViewer({ strState, clientId, navigate }) {
  const [loading, setLoading] = useState(true);
  const [compData, setCompData] = useState(null);
  const [activeSection, setActiveSection] = useState(0);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!strState?.assignment_id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/strategic-review/assignments/${strState.assignment_id}/completion`);
      setCompData(data);
    } catch (e) {
      setError("Could not load submission. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [strState?.assignment_id]);

  useEffect(() => { load(); }, [load]);

  if (!strState?.completion_id) return (
    <EmptyState icon="FileCheck2" title="No submitted STR Organizer" description="This client has not yet submitted a Strategic Tax Review Organizer." />
  );
  if (loading) return <LoadingState label="Loading submitted answers…" />;
  if (error) return <AlertBanner tone="danger" title="Could not load submission">{error}</AlertBanner>;
  if (!compData?.completion) return (
    <EmptyState icon="FileQuestion" title="Submission not found" description="The STR submission could not be located. It may have been archived." />
  );

  const comp = compData.completion;
  const docReqs = compData.document_requests || [];

  // Use submitted_snapshot_state when available — preserves original conditional visibility.
  // Fall back to live-recomputed state if snapshot is missing (legacy completions).
  const viewState = comp.submitted_snapshot_state || compData.state;
  const sections = (viewState?.sections || []).filter((s) => (s.questions || []).some((q) => q.visible));
  const current = sections[activeSection];

  // Count visible questions across all sections (what client was actually presented with).
  const visibleTotal = sections.reduce((n, s) => n + (s.questions || []).filter((q) => q.visible).length, 0);
  const answeredTotal = sections.reduce((n, s) => n + (s.questions || []).filter((q) => q.visible && q.answered).length, 0);

  // Document requests without a verified question link (shown in standalone section).
  const linkedQCodes = new Set((docReqs || []).map((d) => d.origin?.question_code).filter(Boolean));
  const unlinkedDocs = docReqs.filter((d) => !d.origin?.question_code && d.request_status !== "not_applicable");

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <div className="space-y-4" data-testid="str-submission-viewer">
      {/* Submission metadata header */}
      <SectionCard>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Icons.FileCheck2 className="h-5 w-5 text-emerald-600" />
              <h2 className="text-base font-semibold text-slate-900">Submitted STR Organizer</h2>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium border border-emerald-200"
                data-testid="str-sub-status-badge">Submitted</span>
            </div>
            <div className="text-sm text-slate-500 space-y-0.5">
              <div>Submitted: <span className="font-medium text-slate-700" data-testid="str-sub-submitted-at">{fmtDate(comp.submitted_at)}</span></div>
              {comp.certification_name && (
                <div>Certified by: <span className="font-medium text-slate-700" data-testid="str-sub-certified-by">{comp.certification_name}</span></div>
              )}
              <div>Completion ID: <span className="font-mono text-xs text-slate-500" data-testid="str-sub-id">{comp.id?.slice(0, 8)}</span></div>
            </div>
          </div>
          <div className="text-right text-sm">
            <div className="text-2xl font-bold text-slate-800" data-testid="str-sub-answered-count">{answeredTotal}</div>
            <div className="text-slate-400 text-xs">of {visibleTotal} questions answered</div>
            <div className="mt-1 w-32 h-1.5 bg-slate-100 rounded-full overflow-hidden ml-auto">
              <div className="h-full bg-emerald-500" style={{ width: `${visibleTotal > 0 ? Math.round((answeredTotal / visibleTotal) * 100) : 0}%` }} />
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs text-slate-400 flex items-center gap-1.5">
            <Icons.Lock className="h-3.5 w-3.5" />
            Read-only — original submission preserved. No changes can be made to this record.
          </p>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Section navigation sidebar */}
        <aside className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sticky top-4" data-testid="str-sub-section-nav">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Sections</div>
            <nav className="space-y-1">
              {sections.map((s, i) => {
                const done = (s.questions || []).filter((q) => q.visible && q.answered).length;
                const total = (s.questions || []).filter((q) => q.visible).length;
                const allDone = total > 0 && done === total;
                return (
                  <button key={s.key} onClick={() => setActiveSection(i)}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm ${i === activeSection ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`str-sub-section-nav-${s.key}`}>
                    {allDone
                      ? <Icons.CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                      : <Icons.Circle className="h-4 w-4 shrink-0 text-slate-300" />}
                    <span className="truncate">{s.title}</span>
                    <span className="ml-auto text-[10px] text-slate-400 shrink-0">{done}/{total}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Current section questions */}
        <main className="lg:col-span-3">
          {current && (
            <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`str-sub-section-${current.key}`}>
              <div className="flex items-center gap-3 mb-1">
                {(() => { const I = Icons[current.icon] || Icons.FileText; return <I className="h-5 w-5 text-slate-400" />; })()}
                <h2 className="text-lg font-semibold text-slate-900">{current.title}</h2>
              </div>
              {current.intro && <p className="text-slate-500 text-sm mb-6">{current.intro}</p>}
              <div className="space-y-6">
                {(current.questions || []).filter((q) => q.visible).map((q) => (
                  <div key={q.code} className="pb-5 border-b border-slate-50 last:border-0" data-testid={`str-sub-section-q-${q.code}`}>
                    <ReadOnlyAnswerDisplay q={q} docReqs={docReqs} />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-6 pt-5 border-t border-slate-100">
                <button disabled={activeSection === 0} onClick={() => setActiveSection((i) => Math.max(0, i - 1))}
                  className="inline-flex items-center gap-1 text-slate-500 disabled:opacity-40 text-sm"
                  data-testid="str-sub-prev">
                  <Icons.ChevronLeft className="h-4 w-4" />Previous
                </button>
                {activeSection < sections.length - 1 ? (
                  <button onClick={() => setActiveSection((i) => i + 1)}
                    className="inline-flex items-center gap-1 bg-slate-800 hover:bg-slate-700 text-white px-5 py-2.5 rounded-xl text-sm font-medium"
                    data-testid="str-sub-next">
                    Next<Icons.ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <span className="text-xs text-slate-400 italic">End of submission</span>
                )}
              </div>
            </div>
          )}

          {/* Outstanding documents without verified question links */}
          {unlinkedDocs.length > 0 && (
            <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6" data-testid="str-sub-unlinked-docs">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Icons.Paperclip className="h-4 w-4 text-slate-400" />Outstanding Documents
              </h3>
              <div className="space-y-2">
                {unlinkedDocs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5 text-sm"
                    data-testid={`str-sub-unlinked-doc-${d.id}`}>
                    <span className="text-slate-700">{d.title}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${d.request_status === "missing" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{d.request_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function AllisonFindings({ assignments, clientId }) {
  const published = assignments.find((a) => ["portal_ready", "in_progress", "submitted"].includes(a.status));
  const [loading, setLoading] = useState(true);
  const [completion, setCompletion] = useState(null);
  const [run, setRun] = useState(null);
  const [rerunning, setRerunning] = useState(false);
  const [summary, setSummary] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    if (!published) { setLoading(false); return; }
    try {
      const { data: c } = await api.get(`/strategic-review/assignments/${published.id}/completion`);
      setCompletion(c.completion);
      if (c.completion) {
        const [{ data: r }, { data: s }] = await Promise.all([
          api.get(`/strategic-reviews/completion/${c.completion.id}`),
          api.get(`/strategic-reviews/completion/${c.completion.id}/findings/summary`).catch(() => ({ data: null })),
        ]);
        setRun(r.run);
        setSummary(s);
        if (r.run?.status === "running") {
          if (pollRef.current) clearTimeout(pollRef.current);
          pollRef.current = setTimeout(load, 6000);
        }
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [published]);
  useEffect(() => { load(); return () => pollRef.current && clearTimeout(pollRef.current); }, [load]);

  const rerun = async () => {
    setRerunning(true);
    try { await api.post(`/strategic-reviews/completion/${completion.id}/rerun`, {}); toast.success("Allison re-run started"); setTimeout(load, 2000); }
    catch (e) { toast.error("Re-run failed"); } finally { setRerunning(false); }
  };

  const [pkg, setPkg] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadPkg = useCallback(async () => {
    if (!completion) return;
    try { const { data } = await api.get(`/strategic-reviews/completion/${completion.id}/package`); setPkg(data.package); }
    catch { /* none */ }
  }, [completion]);
  useEffect(() => { loadPkg(); }, [loadPkg]);

  const assemble = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/strategic-reviews/completion/${completion.id}/package/assemble`); setPkg(data.package); toast.success("Follow-up package assembled"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Assemble failed"); } finally { setBusy(false); }
  };
  const savePkg = async (items) => {
    setPkg((p) => ({ ...p, items }));
    try { await api.patch(`/strategic-reviews/package/${pkg.id}`, { items }); } catch { toast.error("Save failed"); }
  };
  const updateItem = (id, patch) => savePkg(pkg.items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) => savePkg(pkg.items.filter((it) => it.id !== id));
  const addManual = (kind) => savePkg([...(pkg.items || []), {
    id: `m_${Math.random().toString(36).slice(2, 9)}`, kind, include: true, manual: true, topic_label: "General",
    ...(kind === "supplemental_question" ? { question_text: "", response_type: "long_text" } :
       kind === "document_request" ? { doc_title: "", document_type: "other" } : { ptf_field: "", ptf_value: "" }),
  }]);
  const approve = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/strategic-reviews/package/${pkg.id}/approve`); toast.success(`Round ${data.round} published to the client portal`); setPkg(null); setTimeout(() => { loadPkg(); load(); }, 500); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approve failed"); } finally { setBusy(false); }
  };

  const handleDispositionChange = (fid, disp, notes) => {
    setRun((r) => ({ ...r, findings: r.findings.map((f) => (f.id === fid ? { ...f, disposition: disp, disposition_notes: notes || f.disposition_notes } : f)) }));
    load(); // refresh to sync summary counts
  };

  const handleAddToPackage = (finding) => {
    toast.info(`Added "${finding.summary.slice(0, 40)}…" to Review Package. Assemble the package to include it.`);
    assemble();
  };

  if (loading) return <LoadingState label="Loading Allison's review…" />;
  if (!published) return <EmptyState icon="Compass" title="No published review yet" description="Assemble a Strategic Review and use 'Make Available in Portal' first." />;
  if (!completion) return <EmptyState icon="Send" title="Not in the portal yet" description="Publish the assembled review so the client can complete it." />;
  if (completion.status !== "submitted" && !run) return <EmptyState icon="Clock" title="Waiting on the client" description="Allison analyzes automatically the moment the client submits their questionnaire." />;
  if (!run) return <EmptyState icon="Brain" title="Allison hasn't run yet" description="Analysis starts automatically on submission." />;

  const findings = run.findings || [];
  const grouped = CAT_ORDER.map((c) => ({ cat: c, items: findings.filter((f) => f.category === c) })).filter((g) => g.items.length);
  const rtc = summary?.resolution_type_counts || {};

  return (
    <div className="space-y-5" data-testid="sr-allison-findings">
      <SectionCard title="Allison Strategic Review" icon="Brain"
        action={<ActionButton variant="secondary" icon="RefreshCw" onClick={rerun} loading={rerunning} data-testid="sr-allison-rerun">Re-run Allison</ActionButton>}>
        {run.status === "running" ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Icons.Loader2 className="h-4 w-4 animate-spin" />Allison is analyzing this review…</div>
        ) : run.status === "failed" ? (
          <AlertBanner tone="danger" title="Allison could not complete the review">A staff re-run is available.</AlertBanner>
        ) : (
          <>
            <p className="text-sm text-slate-600" data-testid="sr-allison-summary">{run.summary}</p>
            <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
              <Badge variant="secondary">Profile: {run.analysis_profile === "advanced_strategic_review" ? "Advanced" : "Standard"}</Badge>
              <Badge variant="outline">Model: {run.llm_model}</Badge>
              {run.escalated && <Badge variant="destructive">Escalated: {run.escalation_reason}</Badge>}
              <Badge variant="outline">Confidence: {run.overall_confidence}%</Badge>
              <Badge variant="outline">{findings.length} findings</Badge>
            </div>
            {run.inputs && (
              <p className="text-[11px] text-slate-400 mt-2">
                Based on: questionnaire submitted {run.inputs.questionnaire_submitted_at ? new Date(run.inputs.questionnaire_submitted_at).toLocaleDateString() : "—"} · {run.inputs.documents_reviewed} document(s) · {run.inputs.ptf_facts} PTF fact(s) · Knowledge Center {run.inputs.knowledge_center_as_of} · completed {run.completed_at ? new Date(run.completed_at).toLocaleTimeString() : "—"}
              </p>
            )}
            <p className="text-[11px] text-slate-400 mt-2 italic">Allison is advisory only — she never changes data or contacts the client. Staff disposition &amp; governed follow-up (Round 2) arrive in the next phase.</p>
          </>
        )}
      </SectionCard>

      {/* Phase 1D: Clarifications Summary sidebar panel */}
      {findings.length > 0 && (
        <div className="border border-slate-200 rounded-xl bg-white px-4 py-3" data-testid="sr-clarifications-summary">
          <div className="flex items-center gap-2 mb-3">
            <Icons.SlidersHorizontal className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">Clarifications</span>
            {summary?.unresolved_blocking > 0 && (
              <span className="ml-auto text-[11px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-medium" data-testid="sr-blocking-count">
                {summary.unresolved_blocking} blocking report
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { key: "staff_input",           label: "Staff Input",       icon: "MessageSquarePlus", color: "text-rose-600" },
              { key: "staff_decision",         label: "Staff Decisions",   icon: "CheckSquare",       color: "text-rose-600" },
              { key: "client_question",        label: "Client Questions",  icon: "HelpCircle",        color: "text-amber-600" },
              { key: "client_missing_answer",  label: "Return to Q",       icon: "CornerDownRight",   color: "text-amber-600" },
              { key: "client_document",        label: "Documents",         icon: "Paperclip",         color: "text-orange-600" },
              { key: "review_only",            label: "Review Only",       icon: "Eye",               color: "text-slate-500" },
            ].map(({ key, label, icon, color }) => {
              const count = rtc[key] || 0;
              const I = Icons[icon];
              return (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <I className={`h-3.5 w-3.5 ${color}`} />
                  <span className="text-slate-600">{label}:</span>
                  <span className={`font-semibold ${count > 0 ? "text-slate-800" : "text-slate-300"}`}
                    data-testid={`sr-summary-${key}`}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {grouped.map((g) => {
        const meta = CAT_META[g.cat];
        return (
          <SectionCard key={g.cat} title={`${meta.label} (${g.items.length})`} icon={meta.icon} data-testid={`sr-findings-group-${g.cat}`}>
            <div className="space-y-3">
              {g.items.map((f) => (
                <FindingCard key={f.id} f={f} completionId={completion.id}
                  onDispositionChange={handleDispositionChange}
                  onAddToPackage={handleAddToPackage}
                  onLoad={load}
                  completion={completion}
                  runStatus={run?.status} />
              ))}
            </div>
          </SectionCard>
        );
      })}

      <ReviewPackage pkg={pkg} busy={busy} onAssemble={assemble} onUpdateItem={updateItem}
        onRemove={removeItem} onAddManual={addManual} onApprove={approve} hasFindings={findings.length > 0} clientId={clientId}
        roundInfo={completion?.supplemental_rounds?.find((r) => r.round === pkg?.round) || null} />
    </div>
  );
}

function ReviewPackage({ pkg, busy, onAssemble, onUpdateItem, onRemove, onAddManual, onApprove, hasFindings, clientId, roundInfo }) {
  const KINDS = [
    { kind: "supplemental_question", label: "Supplemental Questions", icon: "HelpCircle" },
    { kind: "document_request", label: "Document Requests", icon: "Paperclip" },
    { kind: "ptf_proposal", label: "PTF Proposals", icon: "Landmark" },
  ];
  if (!pkg || pkg.status === "approved") {
    return (
      <SectionCard title="Review Package" icon="PackageOpen" data-testid="sr-review-package">
        {pkg?.status === "approved" ? (
          <div className="space-y-2">
            {/* Publication fact */}
            <div className="text-sm text-emerald-700 flex items-center gap-2" data-testid="sr-pkg-published">
              <Icons.CheckCircle2 className="h-4 w-4 shrink-0" />
              Round {pkg.round} published — {pkg.published_ref?.supplemental_questions || 0} question(s), {pkg.published_ref?.document_requests || 0} document request(s), {(pkg.published_ref?.ptf_proposals || []).length} PTF proposal(s).
            </div>
            {/* Notification fact — distinct from publication.
                Only rendered when roundInfo is available; older STRs without metadata are silently omitted. */}
            {roundInfo?.notification_status === "sent" ? (
              <div className="text-sm text-emerald-700 flex items-center gap-2" data-testid="sr-pkg-notif-sent">
                <Icons.Mail className="h-4 w-4 shrink-0" />
                Client notified by email{roundInfo.notification_sent_at
                  ? ` — ${new Date(roundInfo.notification_sent_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}`
                  : ""}
              </div>
            ) : roundInfo?.notification_status === "failed" ? (
              <div className="text-sm text-rose-700 flex items-center gap-2" data-testid="sr-pkg-notif-failed">
                <Icons.AlertTriangle className="h-4 w-4 shrink-0" />
                <span><span className="font-semibold">Client notification failed</span> — the automatic email was not sent. The client may not know action is required.</span>
              </div>
            ) : roundInfo ? (
              /* Round exists in canonical data but no notification result recorded yet */
              <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="sr-pkg-notif-pending">
                <Icons.Clock className="h-4 w-4 shrink-0" />
                Client notification pending
              </div>
            ) : null /* Pre-P0 round — no notification metadata; safe silence */ }
            {/* Client response fact — if the round was submitted, show it here too */}
            {roundInfo?.submitted_at && (
              <div className="text-sm text-teal-700 flex items-center gap-2" data-testid="sr-pkg-client-responded">
                <Icons.MessageSquare className="h-4 w-4 shrink-0" />
                Client responded — {new Date(roundInfo.submitted_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">Assemble Allison's recommended client follow-up. You'll review, edit, and approve before anything reaches the client.</p>
            <ActionButton variant="primary" icon="Wand2" onClick={onAssemble} loading={busy} disabled={!hasFindings} data-testid="sr-assemble-package">Assemble Follow-up Package</ActionButton>
          </div>
        )}
      </SectionCard>
    );
  }
  const included = (pkg.items || []).filter((i) => i.include).length;
  return (
    <SectionCard title={`Review Package — Round ${pkg.round}`} icon="PackageOpen" data-testid="sr-review-package"
      action={<ActionButton variant="primary" icon="Send" onClick={onApprove} loading={busy} disabled={!included} data-testid="sr-approve-package">Approve &amp; Publish Round {pkg.round}</ActionButton>}>
      <p className="text-xs text-slate-500 mb-3">Uncheck anything that shouldn't go out, edit wording, or add an item. Nothing reaches the client until you approve. {included} item(s) selected.</p>
      {KINDS.map(({ kind, label, icon }) => {
        const items = (pkg.items || []).filter((i) => i.kind === kind);
        const I = Icons[icon];
        return (
          <div key={kind} className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1"><I className="h-3.5 w-3.5" />{label} ({items.length})</span>
              <button onClick={() => onAddManual(kind)} className="text-[11px] text-blue-600" data-testid={`sr-add-${kind}`}>+ Add manually</button>
            </div>
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.id} className="flex items-start gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`sr-package-item-${it.id}`}>
                  <input type="checkbox" checked={!!it.include} onChange={(e) => onUpdateItem(it.id, { include: e.target.checked })} className="mt-1.5" data-testid={`sr-item-include-${it.id}`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] text-slate-400">{it.topic_label}{it.manual ? " · manual" : ""}{it.confidence != null ? ` · ${it.confidence}%` : ""}</span>
                    {kind === "supplemental_question" && <textarea rows={2} value={it.question_text || ""} onChange={(e) => onUpdateItem(it.id, { question_text: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm mt-1" data-testid={`sr-item-text-${it.id}`} />}
                    {kind === "document_request" && <input value={it.doc_title || ""} onChange={(e) => onUpdateItem(it.id, { doc_title: e.target.value })} className="w-full border border-slate-200 rounded-lg px-2 py-1 text-sm mt-1" data-testid={`sr-item-text-${it.id}`} />}
                    {kind === "document_request" && clientId && <ExistingDocumentReuse clientId={clientId} documentType={it.document_type} />}
                    {kind === "ptf_proposal" && (
                      <div className="flex gap-2 mt-1">
                        <input value={it.ptf_field || ""} onChange={(e) => onUpdateItem(it.id, { ptf_field: e.target.value })} placeholder="field" className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-sm" />
                        <input value={it.ptf_value ?? ""} onChange={(e) => onUpdateItem(it.id, { ptf_value: e.target.value })} placeholder="value" className="flex-1 border border-slate-200 rounded-lg px-2 py-1 text-sm" />
                      </div>
                    )}
                  </div>
                  <button onClick={() => onRemove(it.id)} className="text-rose-500 mt-1" data-testid={`sr-item-remove-${it.id}`}><Icons.X className="h-4 w-4" /></button>
                </div>
              ))}
              {items.length === 0 && <p className="text-xs text-slate-300 italic">None</p>}
            </div>
          </div>
        );
      })}
    </SectionCard>
  );
}


// ── Evidence Summary Panel ────────────────────────────────────────────────────
// Surfaces evidence availability from the canonical STR state response.
// Organizer is always shown as a parallel dimension — never a production gate.
function EvidenceSummaryPanel({ evidenceSummary, strState, className = "" }) {
  const { meeting_transcript_count = 0, meeting_analysis_count = 0,
          has_tax_profile = null, has_organizer = false, organizer_status } = evidenceSummary || {};

  const items = [
    {
      label: "Tax Profile",
      available: has_tax_profile,
      icon: "User",
      detail: has_tax_profile ? "Available" : "Not yet recorded",
    },
    {
      label: "Zoom / Meeting Transcripts",
      available: meeting_transcript_count > 0,
      icon: "Video",
      detail: meeting_transcript_count > 0
        ? `${meeting_transcript_count} transcript${meeting_transcript_count > 1 ? "s" : ""}${meeting_analysis_count > 0 ? `, ${meeting_analysis_count} analyzed by Allison` : " (not yet analyzed)"}`
        : "No transcripts found",
    },
    {
      label: "Allison Meeting Analysis",
      available: meeting_analysis_count > 0,
      icon: "Bot",
      detail: meeting_analysis_count > 0
        ? `${meeting_analysis_count} analysis record${meeting_analysis_count > 1 ? "s" : ""} — included in Evidence Basis`
        : "No meeting analysis yet — analyze transcripts to include meeting intelligence",
    },
    {
      label: "STR Organizer",
      available: has_organizer,
      parallel: true,
      icon: "ClipboardList",
      detail: has_organizer
        ? `Assigned — ${organizer_status === "portal_ready" ? "Portal Ready (awaiting client)" : organizer_status === "submitted" ? "Submitted" : organizer_status || "In progress"}`
        : "Not yet assigned — production may begin without it",
    },
  ];

  const lifecycleSteps = [
    { step: "Generate Evidence Basis", icon: "Database", active: strState?.state === "questionnaire_published" || strState?.state === "review_ready" },
    { step: "Run Allison Analysis", icon: "Bot", active: false },
    { step: "Review Findings", icon: "Search", active: false },
    { step: "Generate STR Draft", icon: "FileText", active: false },
    { step: "Chapter Review", icon: "BookOpen", active: false },
    { step: "Management Approval", icon: "CheckCircle2", active: false },
    { step: "Generate PDF", icon: "Download", active: false },
    { step: "Publish to Portal", icon: "Send", active: false },
  ];

  return (
    <div className={`rounded-xl border border-slate-200 bg-white overflow-hidden ${className}`} data-testid="str-evidence-summary-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
        <Icons.Layers className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">Existing Evidence</span>
        <span className="text-xs text-slate-400 ml-auto">Available at basis-generation time</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100" data-testid="str-evidence-items">
        {items.map((item) => {
          const Icon = Icons[item.icon];
          return (
            <div key={item.label} className="px-3 py-3 flex flex-col gap-1.5" data-testid={`str-evidence-${item.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-1.5">
                <Icon className={`h-3.5 w-3.5 shrink-0 ${item.available ? "text-emerald-600" : "text-slate-300"}`} />
                <span className="text-xs font-medium text-slate-700">{item.label}</span>
                {item.parallel && (
                  <span className="ml-auto text-[9px] text-slate-400 bg-slate-100 px-1 rounded leading-tight">Parallel</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {item.available
                  ? <Icons.CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                  : <Icons.Circle className="h-3 w-3 text-slate-300 shrink-0" />}
                <span className={`text-[11px] leading-tight ${item.available ? "text-emerald-700" : "text-slate-400"}`}>
                  {item.detail}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* STR Lifecycle guidance */}
      <div className="border-t border-slate-100 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Icons.GitBranch className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Production Lifecycle</span>
        </div>
        <div className="flex items-center gap-1 flex-wrap" data-testid="str-lifecycle-steps">
          {lifecycleSteps.map((s, i) => {
            const StepIcon = Icons[s.icon];
            return (
              <React.Fragment key={s.step}>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium
                  ${s.active ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-500"}`}
                  title={s.step}>
                  <StepIcon className="h-2.5 w-2.5 shrink-0" />
                  <span className="hidden sm:inline">{s.step}</span>
                </div>
                {i < lifecycleSteps.length - 1 && (
                  <Icons.ChevronRight className="h-3 w-3 text-slate-300 shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
